// YoYMembershipChart — Long-term strategic membership trajectory.
// PRIMARY: filled area chart showing total active members at year-end (the "arc").
// SECONDARY: dashed line showing "Running" roster (actually staffing apparatus).
// TERTIARY: thin net-change bars on right axis (colored by sign).
// Deliberately different visual language from GainLossChart (flow) — this shows STATE.

import {
  ComposedChart,
  Area,
  Line,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useTheme } from "../../hooks/useTheme";

export interface YoYRow {
  year: number;
  joined: number;
  separated: number;
  net: number;
  /** Running total of active members at end of year (ACTIVE_STATUSES filter) */
  totalActiveAtEnd: number;
  /** Members in "Running" status at year-end — actually staffing apparatus */
  totalRunning?: number;
  /** Optional — provided externally if known, else computed by buildYoYData */
  totalActive?: number;
  /**
   * true = staffing-model estimate (2010-2019, visualised as faded/dotted)
   * false/undefined = data-derived (2020+, full opacity solid dashed)
   */
  isEstimated?: boolean;
}

interface YoYMembershipChartProps {
  rows: YoYRow[];
  isLoading?: boolean;
}

interface CustomTooltipPayload {
  name: string;
  value: number;
  color: string;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: CustomTooltipPayload[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const total = payload.find((p) => p.name === "Active Members");
  const running = payload.find(
    (p) => p.name === "Running Roster" || p.name === "Running (estimated)",
  );
  const isRunningEstimated = running?.name === "Running (estimated)";
  const net = payload.find((p) => p.name === "Net Change");

  const netVal = net?.value ?? 0;

  return (
    <div className="glass rounded-lg px-3 py-2.5 text-xs shadow-lg min-w-[200px]">
      <p className="font-bold text-theme-primary mb-2 text-sm">{label}</p>
      {total && (
        <div className="flex justify-between gap-4">
          <span className="text-theme-muted">Active at Year-End</span>
          <span className="font-bold text-[#0E403A] dark:text-[#4AADA0]">
            {total.value?.toLocaleString()}
          </span>
        </div>
      )}
      {running && running.value > 0 && (
        <div className="flex justify-between gap-4 mt-1">
          <span className="text-theme-muted">
            Running (on apparatus)
            {isRunningEstimated && (
              <span className="ml-1 text-[#D97706] text-[9px] font-semibold">
                est.
              </span>
            )}
          </span>
          <span
            className={`font-bold ${isRunningEstimated ? "text-[#4AADA0] opacity-70" : "text-[#4AADA0]"}`}
          >
            {running.value?.toLocaleString()}
          </span>
        </div>
      )}
      {net !== undefined && (
        <div className="flex justify-between gap-4 mt-1 pt-1 border-t border-theme-subtle">
          <span className="text-theme-muted">Net Change</span>
          <span
            className={`font-bold ${netVal > 0 ? "text-[#16a34a]" : netVal < 0 ? "text-[#dc2626]" : "text-theme-muted"}`}
          >
            {netVal >= 0 ? "+" : ""}
            {netVal}
          </span>
        </div>
      )}
      {total && total.value < 200 && (
        <div className="mt-1.5 text-[10px] text-[#D97706] font-semibold">
          {200 - total.value} below 200-member target
        </div>
      )}
    </div>
  );
}

export default function YoYMembershipChart({
  rows,
  isLoading = false,
}: YoYMembershipChartProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";
  // Brand teal: dark mode gets a lighter variant for legibility
  const areaStroke = isDark ? "#4AADA0" : "#0E403A";
  // Running roster line — slightly lighter teal
  const runningStroke = "#4AADA0";
  const areaFillId = "trajectoryGradient";

  if (isLoading) {
    return (
      <div className="flex items-end gap-1 h-52 px-4 py-4">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="flex-1 flex flex-col gap-1 items-center">
            <div
              className="skeleton w-full rounded-t-sm"
              style={{ height: `${30 + ((i * 13 + 11) % 65)}%` }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-52 gap-2">
        <p className="text-sm text-theme-muted">
          No year-over-year data available
        </p>
        <p className="text-xs text-theme-muted">
          Member history will appear after sync
        </p>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const hasRunning = rows.some((r) => (r.totalRunning ?? 0) > 0);

  // Split Running data into two keys so we can style estimated vs confirmed separately.
  // Recharts connects adjacent non-null values, so where the two series meet at the
  // boundary year (2019-2020) each key carries one side of the transition point,
  // plus one "bridge" point on the other series so the lines connect visually.
  const ESTIMATE_CUTOFF = 2020; // first year that is data-derived (not estimated)
  const chartData = rows.map((r) => {
    const running = r.totalRunning ?? 0;
    const isEst = r.isEstimated === true;
    return {
      ...r,
      label: String(r.year),
      // Estimated segment: populated for 2010-2019 PLUS year 2020 (bridge point)
      totalRunningEstimated:
        (isEst || r.year === ESTIMATE_CUTOFF) && running > 0 ? running : null,
      // Confirmed segment: populated for 2020+ PLUS year 2019 (bridge point)
      totalRunningConfirmed:
        (!isEst || r.year === ESTIMATE_CUTOFF - 1) && running > 0
          ? running
          : null,
    };
  });

  // Y-axis domain: left = total active (and running), right = net change
  const maxTotal = Math.max(...rows.map((r) => r.totalActiveAtEnd), 10);
  const maxAbsNet = Math.max(...rows.map((r) => Math.abs(r.net)), 5);
  const leftMax = Math.ceil(maxTotal * 1.18);
  const rightMax = Math.ceil(maxAbsNet * 2.5); // tall enough that bars don't dwarf the area
  const rightMin = -rightMax;

  return (
    <div className="space-y-2">
      {/* Legend */}
      <div className="flex items-center gap-5 flex-wrap px-2 text-xs text-theme-muted">
        <span className="flex items-center gap-1.5">
          <svg width="20" height="10" className="shrink-0">
            <defs>
              <linearGradient id="legendGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={areaStroke} stopOpacity={0.5} />
                <stop offset="100%" stopColor={areaStroke} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <rect
              x="0"
              y="3"
              width="20"
              height="7"
              fill="url(#legendGrad)"
              rx="1"
            />
            <line
              x1="0"
              y1="3"
              x2="20"
              y2="3"
              stroke={areaStroke}
              strokeWidth="2"
            />
          </svg>
          Active Members (year-end)
        </span>
        {hasRunning && (
          <>
            <span className="flex items-center gap-1.5">
              <svg width="20" height="10" className="shrink-0">
                <line
                  x1="0"
                  y1="5"
                  x2="6"
                  y2="5"
                  stroke={runningStroke}
                  strokeWidth="2"
                  strokeDasharray="4 2"
                />
                <line
                  x1="8"
                  y1="5"
                  x2="14"
                  y2="5"
                  stroke={runningStroke}
                  strokeWidth="2"
                  strokeDasharray="4 2"
                />
                <line
                  x1="16"
                  y1="5"
                  x2="20"
                  y2="5"
                  stroke={runningStroke}
                  strokeWidth="2"
                  strokeDasharray="4 2"
                />
              </svg>
              Running (data)
            </span>
            <span className="flex items-center gap-1.5 opacity-55">
              <svg width="20" height="10" className="shrink-0">
                <line
                  x1="0"
                  y1="5"
                  x2="5"
                  y2="5"
                  stroke={runningStroke}
                  strokeWidth="1.5"
                  strokeDasharray="3 4"
                />
                <line
                  x1="7"
                  y1="5"
                  x2="12"
                  y2="5"
                  stroke={runningStroke}
                  strokeWidth="1.5"
                  strokeDasharray="3 4"
                />
                <line
                  x1="15"
                  y1="5"
                  x2="20"
                  y2="5"
                  stroke={runningStroke}
                  strokeWidth="1.5"
                  strokeDasharray="3 4"
                />
              </svg>
              Running (est.)
            </span>
          </>
        )}
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#16a34a] opacity-70" />
          Net gain
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#dc2626] opacity-70" />
          Net loss
        </span>
        <span className="flex items-center gap-1.5 ml-auto">
          <span className="inline-block w-3 border-t-2 border-dashed border-[#D97706]" />
          <span className="text-[#D97706] font-semibold">Target: 200+</span>
        </span>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart
          data={chartData}
          margin={{ top: 16, right: 40, left: 0, bottom: 4 }}
        >
          <defs>
            <linearGradient id={areaFillId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={areaStroke}
                stopOpacity={isDark ? 0.4 : 0.3}
              />
              <stop offset="85%" stopColor={areaStroke} stopOpacity={0.02} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />

          <XAxis
            dataKey="label"
            tick={{ fill: axisColor, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />

          {/* Left axis — total active members */}
          <YAxis
            yAxisId="left"
            tick={{ fill: axisColor, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={36}
            domain={[0, leftMax]}
          />

          {/* Right axis — net change (hidden tick labels, just for scale) */}
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fill: axisColor, fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            width={38}
            domain={[rightMin, rightMax]}
            tickFormatter={(v: number) =>
              v === 0 ? "0" : v > 0 ? `+${v}` : String(v)
            }
          />

          <Tooltip
            content={<CustomTooltip />}
            cursor={{
              fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
            }}
          />

          {/* Staffing target reference line */}
          <ReferenceLine
            yAxisId="left"
            y={200}
            stroke="#D97706"
            strokeDasharray="5 3"
            strokeWidth={1.5}
            label={{
              value: "200",
              position: "insideTopRight",
              fill: "#D97706",
              fontSize: 10,
              fontWeight: 700,
            }}
          />

          {/* YTD reference line */}
          <ReferenceLine
            yAxisId="left"
            x={String(currentYear)}
            stroke={isDark ? "#3f6b60" : "#a7d3cb"}
            strokeDasharray="4 2"
            strokeWidth={1.5}
            label={{
              value: "YTD",
              position: "insideTopLeft",
              fill: isDark ? "#4ade80" : "#16a34a",
              fontSize: 10,
              fontWeight: 700,
            }}
          />

          {/* July 2018 inflection point — 56-hour work week introduced */}
          {rows.some((r) => r.year === 2018) && (
            <ReferenceLine
              yAxisId="left"
              x="2018"
              stroke="#DC2626"
              strokeDasharray="4 4"
              strokeWidth={2}
              label={{
                value: "56hr Work Week",
                position: "top",
                fill: "#DC2626",
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          )}

          {/* Net-change bars — SECONDARY, right axis, color by sign */}
          <Bar
            yAxisId="right"
            dataKey="net"
            name="Net Change"
            maxBarSize={28}
            opacity={0.65}
            radius={[2, 2, 0, 0]}
          >
            {rows.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.net >= 0 ? "#16a34a" : "#dc2626"}
              />
            ))}
          </Bar>

          {/* Total active area — PRIMARY, left axis */}
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="totalActiveAtEnd"
            name="Active Members"
            stroke={areaStroke}
            strokeWidth={3}
            fill={`url(#${areaFillId})`}
            dot={{ r: 4, fill: areaStroke, strokeWidth: 0 }}
            activeDot={{
              r: 6,
              fill: areaStroke,
              strokeWidth: 2,
              stroke: isDark ? "#18181b" : "#fff",
            }}
          />

          {/* Running roster — SECONDARY, left axis.
               Two separate Line components for visual distinction:
               · Estimated (2010-2019): faded + dotted = staffing-model values
               · Confirmed (2020+): full opacity + dashed = data-derived values     */}
          {hasRunning && (
            <>
              {/* Estimated segment — faded, more dotted */}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="totalRunningEstimated"
                name="Running (estimated)"
                stroke={runningStroke}
                strokeWidth={1.5}
                strokeDasharray="3 4"
                strokeOpacity={0.45}
                dot={{
                  r: 2.5,
                  fill: runningStroke,
                  strokeWidth: 0,
                  fillOpacity: 0.45,
                }}
                activeDot={{
                  r: 4,
                  fill: runningStroke,
                  strokeWidth: 2,
                  stroke: isDark ? "#18181b" : "#fff",
                  fillOpacity: 0.7,
                }}
                connectNulls={false}
              />
              {/* Confirmed segment — full opacity, standard dashed */}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="totalRunningConfirmed"
                name="Running Roster"
                stroke={runningStroke}
                strokeWidth={2}
                strokeDasharray="5 3"
                dot={{ r: 3, fill: runningStroke, strokeWidth: 0 }}
                activeDot={{
                  r: 5,
                  fill: runningStroke,
                  strokeWidth: 2,
                  stroke: isDark ? "#18181b" : "#fff",
                }}
                connectNulls={false}
              />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
